import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../contexts/AuthContext";
import { Mic, Type, Clock, Lock, Heart, Phone, Sparkles, MessageCircle } from "lucide-react";
import { AuntieAvatar, UncAvatar, OleGirlAvatar, YKAvatar } from "../components/Avatars";

export default function LandingPage() {
  const [accepted, setAccepted] = useState(false);
  const navigate = useNavigate();
  const { user, acceptDisclaimer } = useAuth();

  const handleContinue = async () => {
    if (!accepted) return;
    
    if (user) {
      await acceptDisclaimer();
      navigate("/persona");
    } else {
      navigate("/auth", { state: { disclaimerAccepted: true } });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Hero Section */}
      <section className="relative px-6 py-10 overflow-hidden">
        {/* Soft gradient accents */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute top-20 right-0 w-48 h-48 bg-secondary/10 rounded-full blur-3xl translate-x-1/2" />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 text-center"
        >
          <h1 className="font-serif text-5xl font-bold tracking-tight text-foreground mb-2">
            Ventaphy<span className="text-primary">™</span>
          </h1>
          <p className="text-2xl font-serif text-primary font-semibold mb-6">
            Vent to Me.
          </p>
          
          <div className="space-y-1 text-muted-foreground mb-6">
            <p className="text-lg">For the strong friend.</p>
            <p className="text-lg">The one who holds it all together.</p>
          </div>
        </motion.div>
      </section>

      {/* Main Message */}
      <section className="px-6 py-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-primary/5 via-card to-secondary/5 rounded-3xl p-6 border border-primary/10"
        >
          <div className="space-y-3 text-center">
            <p className="text-foreground">Some of us don't need therapy.</p>
            <p className="text-foreground">We don't need to be analyzed.</p>
            <p className="text-foreground">We don't need someone fixing us.</p>
            <div className="py-3">
              <p className="text-lg font-semibold text-primary">
                We just need to let it out—
              </p>
              <p className="text-muted-foreground">
                so we don't carry it into everything else.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* What is Ventaphy */}
      <section className="px-6 py-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-center text-foreground leading-relaxed mb-5 text-sm">
            <span className="font-serif font-bold text-primary">Ventaphy™</span> is a safe, private space to release.
            Speak or type what's on your chest and be heard by our personas—AI characters designed to listen without judgment, 
            validate without diagnosing, and hold space without giving feedback unless you ask.
          </p>
          
          {/* Persona Preview - 4 personas */}
          <div className="flex justify-center gap-4 mb-5">
            <motion.div whileHover={{ scale: 1.05 }} className="text-center">
              <AuntieAvatar size="sm" />
              <p className="text-xs font-semibold text-primary mt-1">Auntie™</p>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} className="text-center">
              <UncAvatar size="sm" />
              <p className="text-xs font-semibold text-amber-700 mt-1">Unc™</p>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} className="text-center">
              <OleGirlAvatar size="sm" />
              <p className="text-xs font-semibold text-orange-600 mt-1">Ole Girl™</p>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} className="text-center">
              <YKAvatar size="sm" />
              <p className="text-xs font-semibold text-amber-800 mt-1">YK™</p>
            </motion.div>
          </div>
          
          {/* Core statements */}
          <div className="bg-card rounded-2xl p-4 border border-border space-y-1 text-center">
            <p className="text-muted-foreground text-sm">This is <span className="text-foreground font-semibold">not</span> therapy.</p>
            <p className="text-muted-foreground text-sm">This is <span className="text-foreground font-semibold">not</span> crisis care.</p>
            <p className="text-primary font-bold">This is release.</p>
          </div>
        </motion.div>
      </section>

      {/* Choose How You Vent */}
      <section className="px-6 py-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="font-serif text-xl font-bold text-center mb-4">
            Choose How You Vent
          </h2>
          
          <div className="space-y-3">
            {/* Standard Vent */}
            <div className="bg-card rounded-2xl p-4 border border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <Heart className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Standard Vent</h3>
                  <p className="text-xs text-primary">Listening Mode</p>
                </div>
              </div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>• AI listens and responds with empathy</li>
                <li>• No feedback. No fixing. No direction</li>
                <li>• Pure emotional release</li>
              </ul>
            </div>
            
            {/* Interactive Vent */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-4 border border-primary/20">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Interactive Vent</h3>
                  <p className="text-xs text-primary">Premium • $9.99/mo</p>
                </div>
                <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  💜 Full
                </span>
              </div>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>• Two-way conversation with personas</li>
                <li>• Feedback only when YOU ask for it</li>
                <li>• Deeper engagement, on your terms</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </section>

      {/* How It Works */}
      <section className="px-6 py-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <h2 className="font-serif text-xl font-bold text-center mb-4">
            How It Works
          </h2>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gradient-to-br from-primary/5 to-transparent rounded-xl p-3 text-center border border-primary/10">
              <Mic className="w-6 h-6 text-primary mx-auto mb-1" />
              <p className="text-xs font-medium">Voice or text</p>
            </div>
            <div className="bg-gradient-to-br from-secondary/5 to-transparent rounded-xl p-3 text-center border border-secondary/10">
              <Clock className="w-6 h-6 text-secondary mx-auto mb-1" />
              <p className="text-xs font-medium">Timed sessions</p>
            </div>
            <div className="bg-gradient-to-br from-accent/5 to-transparent rounded-xl p-3 text-center border border-accent/10">
              <Lock className="w-6 h-6 text-accent mx-auto mb-1" />
              <p className="text-xs font-medium">Nothing saved</p>
            </div>
            <div className="bg-gradient-to-br from-primary/5 to-transparent rounded-xl p-3 text-center border border-primary/10">
              <Sparkles className="w-6 h-6 text-primary mx-auto mb-1" />
              <p className="text-xs font-medium">Release & reset</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Important Note & Disclaimer */}
      <section className="px-6 py-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="space-y-3"
        >
          {/* Important Note */}
          <div className="bg-muted/30 rounded-2xl p-4 border border-border">
            <h3 className="font-semibold text-xs mb-2 text-muted-foreground">Important Note</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Ventaphy™ uses AI-generated personas for emotional support and reflection. 
              These personas are not real people and do not provide medical, mental health, 
              legal, or crisis services. If you are in immediate danger or distress, please 
              contact local emergency services or a crisis hotline.
            </p>
          </div>
          
          {/* Crisis Resources */}
          <div className="bg-red-50 border border-red-100 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">In Crisis?</p>
                <a 
                  href="tel:988" 
                  className="text-primary font-bold hover:underline"
                  data-testid="crisis-hotline-link"
                >
                  Call or Text 988
                </a>
                <p className="text-xs text-muted-foreground">Suicide & Crisis Lifeline</p>
              </div>
            </div>
          </div>

          {/* Acceptance */}
          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="disclaimer"
              checked={accepted}
              onCheckedChange={setAccepted}
              className="mt-0.5"
              data-testid="disclaimer-checkbox"
            />
            <label 
              htmlFor="disclaimer" 
              className="text-xs text-muted-foreground cursor-pointer leading-relaxed"
            >
              I understand that Ventaphy™ is not a substitute for professional mental health care, 
              and I agree to seek appropriate help if I'm in crisis.
            </label>
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="px-6 py-6 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Button
            onClick={handleContinue}
            disabled={!accepted}
            className="w-full h-14 rounded-full text-lg font-semibold bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-primary/20"
            data-testid="continue-btn"
          >
            I'm Ready to Vent
          </Button>
          
          <p className="text-center text-xs text-muted-foreground mt-4">
            Already have an account?{" "}
            <button 
              onClick={() => navigate("/auth")}
              className="text-primary hover:underline"
            >
              Sign in
            </button>
          </p>
        </motion.div>
      </section>
    </div>
  );
}
